import {
    Box,
    Grid,
    Button,
    Select,
    InputLabel,
    FormControl,
    MenuItem,
    Pagination,
    Typography,
} from '@mui/material';
import { FaMagnifyingGlass, FaPlus } from 'react-icons/fa6';
import ProductItem from './components/ProductItem';
import { getProducts } from '../../../../helpers/httpRequests/products';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import useDebouncedValue from '../../../../hooks/useDebouncedValue';
import LoadingComponent from '../../../../components/LoadingComponent';
import EmptyState from '../../../../components/EmptyState';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthProvider';
import { getCategories } from '../../../../helpers/httpRequests/product_props';
import { formatOptions } from '../../../../utils/functions';
import SearchField from '../../../../components/ui/SearchField';

const ITEMS_PER_PAGE = 12;

export default function ProductGrid({ isCustomerStoreGrid, customerData }) {
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const [page, setPage] = useState(1);
    const listRef = useRef(null);

    const { isAuthenticated } = useAuth();
    const searchDebounced = useDebouncedValue(search, 300);

    const listFilters = {
        page,
        pageSize: ITEMS_PER_PAGE,
        search: searchDebounced,
        category,
    };

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['getProducts', listFilters],
        queryFn: () => getProducts(listFilters),
        staleTime: 1000 * 30,
        placeholderData: keepPreviousData,
    });
    const categoriesQuery = useQuery({ queryKey: ['getCategories'], queryFn: getCategories, staleTime: 1000 * 60 });

    const products = useMemo(() => data?.data?.data || [], [data]);
    const pagination = data?.data?.pagination;
    const serverPaged = Boolean(pagination);
    const total = pagination?.total ?? products.length;
    const totalPages = Math.max(1, pagination?.totalPages ?? Math.ceil(total / ITEMS_PER_PAGE));
    const currentPage = Math.min(page, totalPages);
    const from = total === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const to = Math.min(currentPage * ITEMS_PER_PAGE, total);
    const pageItems = serverPaged
        ? products
        : products.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    const categories = formatOptions(categoriesQuery.data);

    const handleSearch = (event) => {
        setSearch(event.target.value);
        setPage(1);
    };

    const handleCategory = (event) => {
        setCategory(event.target.value);
        setPage(1);
    };

    const handlePage = (_, value) => {
        setPage(value);
        listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (isLoading) {
        return <LoadingComponent />;
    }

    return (
        <Box ref={listRef} id="lista-produtos" sx={styles.container}>
            <Box sx={styles.toolbar}>
                <SearchField
                    sx={styles.searchBar}
                    placeholder="Buscar por nome, referência ou categoria…"
                    value={search}
                    onChange={handleSearch}
                />
                <FormControl size="small" sx={styles.category}>
                    <InputLabel>Categoria</InputLabel>
                    <Select label="Categoria" value={category} onChange={handleCategory}>
                        <MenuItem value="">Todas</MenuItem>
                        {categories.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {isAuthenticated && !isCustomerStoreGrid && (
                    <Button
                        component={Link}
                        to="/servico-novo"
                        startIcon={<FaPlus />}
                        variant="contained"
                        color="secondary"
                        sx={styles.serviceBtn}
                    >
                        Novo serviço
                    </Button>
                )}
            </Box>

            {products.length === 0 ? (
                total === 0 && !searchDebounced && !category ? (
                    <EmptyState
                        icon={<FaMagnifyingGlass />}
                        title="Nenhuma joia no catálogo"
                        description="Quando houver peças disponíveis, elas aparecem aqui."
                    />
                ) : (
                    <EmptyState
                        icon={<FaMagnifyingGlass />}
                        title="Nenhuma joia encontrada"
                        description="Tente ajustar a busca ou a categoria."
                    />
                )
            ) : (
                <>
                    <Typography sx={{ ...styles.count, opacity: isFetching ? 0.6 : 1 }}>
                        {from} a {to} de {total}
                    </Typography>
                    <Grid container spacing={2}>
                        {pageItems.map((product) => (
                            <ProductItem
                                key={product.id}
                                {...product}
                                isCustomerStoreGrid={isCustomerStoreGrid}
                                customerData={customerData}
                            />
                        ))}
                    </Grid>
                    {totalPages > 1 && (
                        <Box sx={styles.paginationBox}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePage}
                                color="primary"
                                shape="rounded"
                                siblingCount={0}
                                boundaryCount={1}
                                aria-label="Paginação do catálogo"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}

const styles = {
    container: {
        my: 3,
        minHeight: '60vh',
        scrollMarginTop: { xs: '5.5rem', md: '6rem' },
    },
    toolbar: {
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
    },
    searchBar: {
        flex: 1,
        minWidth: { xs: '100%', sm: 220 },
    },
    category: {
        width: { xs: '100%', sm: 220 },
    },
    serviceBtn: {
        width: { xs: '100%', sm: 'auto' },
        flexShrink: 0,
    },
    count: {
        mt: 3,
        mb: 1.5,
        fontSize: '0.75rem',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'text.secondary',
    },
    paginationBox: {
        display: 'flex',
        justifyContent: 'center',
        pt: 4,
    },
};
